import { useEffect, useRef, useState } from "react";
import styles from "./CoverflowGallery.module.css";

export type CoverImage = { src: string; name: string };

type CoverflowGalleryProps = {
  images: CoverImage[];
  /** Absolute index into `images` for the selected (centered) tile. */
  onSelect: (index: number) => void;
  /** When true (e.g. lightbox open), input is ignored. */
  paused?: boolean;
};

const DEPTH = 260; // how far back each step recedes (px)
const MAXROT = 50; // degrees a fully off-center card is turned away
const WINDOW = 3.6; // how many cards each side stay rendered

/** Shortest signed distance from `d` wrapped into a ring of size `n`. */
const wrapSigned = (d: number, n: number) => {
  let m = ((d % n) + n) % n;
  if (m > n / 2) m -= n;
  return m;
};

/** Smoothstep on [0,1] — ease-in-out used to ramp rotation off the center. */
const smoothstep = (t: number) => {
  const c = Math.min(1, Math.max(0, t));
  return c * c * (3 - 2 * c);
};

/**
 * Horizontal, scroll-jacked coverflow. The centered photo is largest and in
 * front; neighbors recede behind it to the left and right. Scrolling (or
 * dragging, or the arrows) slides the next photo into the center and pushes the
 * current one back. Infinite wrap. Client-only / motion-gated — the accessible
 * grid fallback lives in GalleryPage.
 */
export function CoverflowGallery({ images, onSelect, paused = false }: CoverflowGalleryProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const imagesRef = useRef(images);
  const versionRef = useRef(0);
  const pausedRef = useRef(paused);
  const onSelectRef = useRef(onSelect);
  const apiRef = useRef<{ step: (n: number) => void } | null>(null);

  const [hintHidden, setHintHidden] = useState(false);
  const [center, setCenter] = useState(0);

  const len = images.length;

  if (cardRefs.current.length !== len) {
    cardRefs.current = new Array(len).fill(null);
  }

  useEffect(() => {
    imagesRef.current = images;
    versionRef.current += 1;
  }, [images]);
  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);
  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    let pos = 0;
    let target = 0;
    let raf = 0;
    let lastInput = performance.now() - 9999;
    let lastVersion = -1;
    let lastCenter = -1;
    let interacted = false;

    const spacing = () => Math.min(window.innerWidth * 0.17, 300);

    const noteInput = () => {
      lastInput = performance.now();
      if (!interacted) {
        interacted = true;
        setHintHidden(true);
      }
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (pausedRef.current) return;
      target += (e.deltaY + e.deltaX) * 0.0032;
      noteInput();
    };
    let lastTX = 0;
    let lastTY = 0;
    const onTouchStart = (e: TouchEvent) => {
      lastTX = e.touches[0]?.clientX ?? 0;
      lastTY = e.touches[0]?.clientY ?? 0;
    };
    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      if (pausedRef.current) return;
      const x = e.touches[0]?.clientX ?? lastTX;
      const y = e.touches[0]?.clientY ?? lastTY;
      target += (lastTX - x + (lastTY - y)) * 0.011;
      lastTX = x;
      lastTY = y;
      noteInput();
    };
    const onKey = (e: KeyboardEvent) => {
      if (pausedRef.current) return;
      if (e.key === "ArrowRight") {
        target = Math.round(target) + 1;
        noteInput();
      } else if (e.key === "ArrowLeft") {
        target = Math.round(target) - 1;
        noteInput();
      } else if (e.key === "Enter") {
        const c = ((Math.round(pos) % len) + len) % len;
        onSelectRef.current(c);
      }
    };

    // Tap detection (cards animate, so native click is unreliable).
    let downX = 0;
    let downY = 0;
    let downT = 0;
    const onPointerDown = (e: PointerEvent) => {
      downX = e.clientX;
      downY = e.clientY;
      downT = performance.now();
    };
    const onPointerUp = (e: PointerEvent) => {
      if (pausedRef.current) return;
      const dist = Math.hypot(e.clientX - downX, e.clientY - downY);
      if (dist > 12 || performance.now() - downT > 700) return;
      const el = (e.target as HTMLElement | null)?.closest(
        "[data-card-index]",
      ) as HTMLElement | null;
      if (!el) return;
      const idx = Number(el.dataset.cardIndex);
      if (Number.isNaN(idx)) return;
      const off = wrapSigned(idx - pos, len);
      if (Math.abs(off) < 0.6) {
        onSelectRef.current(idx); // already centered → expand
      } else {
        target = pos + off; // bring it to center
        noteInput();
      }
    };

    apiRef.current = {
      step: (n: number) => {
        target = Math.round(target) + n;
        noteInput();
      },
    };

    stage.addEventListener("wheel", onWheel, { passive: false });
    stage.addEventListener("touchstart", onTouchStart, { passive: true });
    stage.addEventListener("touchmove", onTouchMove, { passive: false });
    stage.addEventListener("pointerdown", onPointerDown);
    stage.addEventListener("pointerup", onPointerUp);
    window.addEventListener("keydown", onKey);

    const tick = () => {
      if (lastVersion !== versionRef.current) {
        lastVersion = versionRef.current;
        pos = 0;
        target = 0;
      }
      const n = imagesRef.current.length || 1;
      const SP = spacing();

      // Snap toward the nearest card once input settles.
      if (performance.now() - lastInput > 140) {
        target += (Math.round(target) - target) * 0.16;
      }
      // Snappier chase so the deck passes through the in-between state quickly
      // and settles cleanly on a centered photo.
      pos += (target - pos) * 0.19;

      for (let i = 0; i < n; i++) {
        const el = cardRefs.current[i];
        if (!el) continue;
        const off = wrapSigned(i - pos, n);
        const a = Math.abs(off);
        if (a > WINDOW) {
          el.style.visibility = "hidden";
          el.style.pointerEvents = "none";
          continue;
        }
        const dir = off < 0 ? -1 : 1;
        // Rotation ramps in quickly off the center (smoothstep over the first
        // step) then keeps turning gently — side cards angle clearly away so
        // they read as a 3D fan instead of overlapping flat planes.
        const turn = a <= 1 ? MAXROT * smoothstep(a) : MAXROT + (a - 1) * 4;
        const rotY = -dir * Math.min(turn, 64);
        const x = off * SP;
        const z = -a * DEPTH;
        const scale = Math.max(0.5, 1 - a * 0.14);
        const opacity = a > 2.6 ? Math.max(0, 1 - (a - 2.6) / 1) : 1;
        const bright = Math.max(0.5, 1 - a * 0.12);
        el.style.visibility = "visible";
        el.style.transform =
          `translate(-50%, -50%) translate3d(${x.toFixed(1)}px, 0, ${z.toFixed(1)}px) ` +
          `rotateY(${rotY.toFixed(2)}deg) scale(${scale.toFixed(3)})`;
        el.style.opacity = opacity.toFixed(3);
        el.style.filter = `brightness(${bright.toFixed(3)})`;
        el.style.pointerEvents = opacity > 0.35 ? "auto" : "none";
      }

      const c = ((Math.round(pos) % n) + n) % n;
      if (c !== lastCenter) {
        lastCenter = c;
        setCenter(c);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      stage.removeEventListener("wheel", onWheel);
      stage.removeEventListener("touchstart", onTouchStart);
      stage.removeEventListener("touchmove", onTouchMove);
      stage.removeEventListener("pointerdown", onPointerDown);
      stage.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      apiRef.current = null;
    };
  }, [len]);

  const centerName = images[center]?.name ?? "";

  return (
    <div
      ref={stageRef}
      className={styles.stage}
      role="region"
      aria-label="Photo gallery — scroll or drag to browse"
    >
      <div className={styles.track}>
        {images.map((img, i) => (
          <button
            key={img.src}
            type="button"
            className={styles.card}
            data-card-index={i}
            aria-label={`View ${img.name}`}
            ref={(el) => {
              cardRefs.current[i] = el;
            }}
          >
            <img className={styles.img} src={img.src} alt={img.name} decoding="async" />
          </button>
        ))}
      </div>

      <div className={styles.vignette} aria-hidden="true" />

      <button
        type="button"
        className={`${styles.nav} ${styles.prev}`}
        aria-label="Previous photo"
        onClick={() => apiRef.current?.step(-1)}
      >
        ‹
      </button>
      <button
        type="button"
        className={`${styles.nav} ${styles.next}`}
        aria-label="Next photo"
        onClick={() => apiRef.current?.step(1)}
      >
        ›
      </button>

      {hintHidden ? (
        <span className={styles.caption}>
          <span>{centerName}</span>
          <span className={styles.counter}>
            {len ? center + 1 : 0} / {len}
          </span>
        </span>
      ) : (
        <span className={styles.hint}>Scroll to browse</span>
      )}
    </div>
  );
}
